import React from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { CLLogo } from 'assets';

export interface SentMessage {
  sendDate: string;
  type: string;
  title: string;
  message: string;
  isRead: boolean;
}

interface CLNotificationsProps {
  messages: SentMessage[];
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoading?: boolean;
}

const typeMap: Record<string, { label: string; color: string; bg: string; }> = {
  'MEDICAL_GUIDE': {
    label: '진료안내',
    color: '#5C25B6',
    bg: 'rgba(153, 106, 229, 0.12)'
  },
  'CALORIE_MANAGEMENT': {
    label: '칼로리 관리',
    color: '#2563EB',
    bg: 'rgba(59, 130, 246, 0.12)'
  },
  'ETC': {
    label: '기타',
    color: '#01A0B5',
    bg: 'rgba(0, 192, 218, 0.12)'
  },
};

// sendDate를 날짜별로 그룹핑하는 함수
const groupMessagesByDate = (messages: SentMessage[]) => {
  // messages가 유효하지 않은 경우 빈 배열 반환
  if (!messages || !Array.isArray(messages)) {
    console.log('Invalid messages, returning empty array');
    return [];
  }

  const grouped: { [key: string]: SentMessage[] } = {};
  
  messages.forEach((msg, index) => {
    const date = moment(msg.sendDate, 'YYYYMMDDHHmmss').format('YYYY년 M월 D일 (ddd)');
    if (!grouped[date]) {
      grouped[date] = [];
    }
    grouped[date].push(msg);
  });
  
  console.log('Grouped before sorting:', grouped);
  
  // 날짜별로 정렬 (최신순)
  const result = Object.entries(grouped)
    .sort(([a], [b]) => moment(b, 'YYYY년 M월 D일 (ddd)').valueOf() - moment(a, 'YYYY년 M월 D일 (ddd)').valueOf())
    .map(([date, items]) => ({ date, items }));
    
  console.log('Final result:', result);
  return result;
};

const CLNotifications: React.FC<CLNotificationsProps> = ({ messages, onLoadMore, hasMore = false, isLoading = false }) => {
  const scrollAreaRef = React.useRef<HTMLDivElement>(null);

  const handleScroll = React.useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    
    // 스크롤이 하단에 도달했을 때 (여유 50px)
    if (scrollHeight - scrollTop - clientHeight < 50 && hasMore && !isLoading && onLoadMore) {
      onLoadMore();
    }
  }, [hasMore, isLoading, onLoadMore]);

  if (!messages || messages.length === 0) {
    return (
      <Wrap>
        <MessagesTitle>전송된 메시지</MessagesTitle>
        <MessagesWrapper>
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            전송된 메시지가 없습니다.
          </div>
        </MessagesWrapper>
      </Wrap>
    );
  }

  const groupedMessages = groupMessagesByDate(messages);

  return (
    <Wrap>
      <MessagesTitle>전송된 메시지</MessagesTitle>
      <MessagesWrapper>
        <ScrollArea ref={scrollAreaRef} onScroll={handleScroll}>
          {groupedMessages.map((group, idx) => (
            <DateGroup key={group.date + idx}>
              <DateText>{group.date}</DateText>
              <MessageList>
                {group.items.map((msg, i) => {
                  const typeInfo = typeMap[msg.type] || typeMap['ETC'];
                  const time = moment(msg.sendDate, 'YYYYMMDDHHmmss').format('A h:mm');
                  return (
                    <MessageCard key={i} $bg={typeInfo.bg} $isRead={msg.isRead}>
                      <MessageHeader>
                        <IconWrapper>
                          <Icon src={CLLogo} alt='CLLogo' />
                        </IconWrapper>
                        <Label $color={typeInfo.color} $bg={typeInfo.bg}>{typeInfo.label}</Label>
                        <MessageTime>{time}</MessageTime>
                      </MessageHeader>
                      <MessageTitle>{msg.title}</MessageTitle>
                      <MessageContent>{msg.message}</MessageContent>
                    </MessageCard>
                  );
                })}
              </MessageList>
            </DateGroup>
          ))}
        </ScrollArea>
        {isLoading && (
          <LoadingContainer>
            <LoadingText>로딩 중...</LoadingText>
          </LoadingContainer>
        )}
      </MessagesWrapper>
    </Wrap>
  );
};

export default CLNotifications;

// 스타일드 컴포넌트
const Wrap = styled.div`
  width: 100%;
  margin-top: 60px;
`;
const MessagesTitle = styled.div`
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 20px;
`;
const MessagesWrapper = styled.div`
  background: #FAFAFB;
  border-radius: 8px;
  border: 1px solid #ECEDEF;
  padding: 32px 32px 24px 32px;
  min-height: 420px;
  max-height: 720px;
  overflow: hidden;
  align-items: flex-start;
  justify-content: space-between;
`;
const ScrollArea = styled.div`
  max-height: 720px;
  overflow-y: auto;
  padding-right: 8px;
`;
const DateGroup = styled.div`
  & + & {
    margin-top: 32px;
  }
`;
const DateText = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #222;
  margin-bottom: 18px;
`;
const MessageList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const MessageCard = styled.div<{ $bg: string; $isRead: boolean }>`
  background: ${({ $isRead, $bg }) => $isRead ? '#FFF' : '#F0F6FF'};
  border-radius: 12px;
  padding: 20px 24px 18px 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  width: 724px;
`;
const MessageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`;
const IconWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 22px;
`;

const Icon = styled.img`
    width: 22px;
    height: 22px;
`;
const Label = styled.span<{ $color: string; $bg: string }>`
  display: flex;
  padding: 4px 12px;
  justify-content: center;
  align-items: center;
  gap: 0px;
  border-radius: 9999px;
  background: ${({ $bg }) => $bg};
  font-size: 13px;
  font-weight: 600;
  color: ${({ $color }) => $color};
  margin-right: 8px;
`;
const MessageTime = styled.span`
  margin-left: auto;
  font-size: 13px;
  color: #999999;
  font-weight: 500;
`;
const MessageTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #222;
  margin-bottom: 2px;
  margin-left: 36px;
`;
const MessageContent = styled.div`
  font-size: 14px;
  color: #444;
  line-height: 1.6;
  white-space: pre-line;
  margin-left: 36px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const LoadingText = styled.div`
  font-size: 14px;
  color: #999;
`; 